import React from "react";
import { Link } from "react-router-dom";
import { Smartphone, Bell, KeyRound, Home, Building2, ArrowRight, Wrench, PoundSterling, FileText, CalendarCheck, Search, LineChart, Camera, MessageSquare, ShieldCheck, TrendingUp } from "lucide-react";

const ROLES = [
  {
    key: "tenants", label: "Tenants", icon: KeyRound,
    benefits: [
      { icon: Wrench, text: "Report maintenance and track every repair to completion" },
      { icon: PoundSterling, text: "Pay rent and see your payment history at a glance" },
      { icon: FileText, text: "Access your tenancy agreement and documents anytime" },
      { icon: Bell, text: "Instant alerts for viewings, renewals and updates" },
    ],
  },
  {
    key: "landlords", label: "Landlords", icon: Building2,
    benefits: [
      { icon: LineChart, text: "Live portfolio dashboard with rent received and arrears" },
      { icon: FileText, text: "Monthly owner statements delivered straight to you" },
      { icon: ShieldCheck, text: "Automatic EPC, Gas & EICR compliance reminders" },
      { icon: Wrench, text: "Approve works and track contractors in real time" },
    ],
  },
  {
    key: "buyers", label: "Buyers", icon: Home,
    benefits: [
      { icon: Search, text: "Saved searches with instant new-listing alerts" },
      { icon: CalendarCheck, text: "Book and manage viewings in a couple of taps" },
      { icon: TrendingUp, text: "Track your purchase from offer to completion" },
      { icon: MessageSquare, text: "Message your agent and get quick answers" },
    ],
  },
  {
    key: "vendors", label: "Vendors", icon: PoundSterling,
    benefits: [
      { icon: CalendarCheck, text: "See every viewing and buyer feedback as it happens" },
      { icon: TrendingUp, text: "Track offers and sale progression in real time" },
      { icon: LineChart, text: "Live marketing performance across every portal" },
      { icon: Camera, text: "All your professional photography in one archive" },
    ],
  },
];

const AppShowcase = () => (
  <section className="relative py-24 bg-[#1c1c1e] overflow-hidden" data-testid="app-showcase">
    <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #d9b98a 1px, transparent 0)", backgroundSize: "26px 26px" }} />
    <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
      <div className="max-w-[720px] mb-14">
        <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-3 flex items-center gap-2"><Smartphone size={15} /> The Anadear App</p>
        <Link to="/app" data-testid="app-showcase-heading" className="font-display text-white text-[32px] md:text-[46px] font-300 leading-tight mb-5 inline-block hover:text-[#f0e3d0] transition-colors">Your move, in the palm of your hand</Link>
        <p className="text-white/60 text-[15px] md:text-[16px] leading-relaxed">
          Whether you're renting, letting, buying or selling, the Anadear app keeps everything in sync
          with our team in real time — no missed messages, no chasing paperwork. Everything you see on the
          web is right there in your pocket.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="app-role-grid">
        {ROLES.map((r) => (
          <div key={r.key} data-testid={`app-role-${r.key}`} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur-sm hover:bg-white/[0.07] hover:border-[#b8935f]/40 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-[#b8935f]/15 flex items-center justify-center mb-4">
              <r.icon size={20} className="text-[#d9b98a]" />
            </div>
            <h3 className="font-display text-white text-[20px] font-400 mb-4">{r.label}</h3>
            <ul className="space-y-3">
              {r.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <b.icon size={15} className="text-[#b8935f] mt-0.5 shrink-0" />
                  <span className="text-[13px] text-white/70 leading-relaxed">{b.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center gap-4">
        <Link to="/app" data-testid="app-showcase-learn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">
          Explore the app <ArrowRight size={16} />
        </Link>
        <Link to="/portal" data-testid="app-showcase-portal" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white text-[14px] font-500 hover:bg-white/10 transition-colors">
          Open your portal
        </Link>
        <span className="text-white/40 text-[13px]">Free for every Anadear client — syncs instantly with our team.</span>
      </div>
    </div>
  </section>
);

export default AppShowcase;
